import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  MapPin, Plus, Trash2, Star, Check, AlertCircle, 
  Home, Building, Package, Edit2, X
} from 'lucide-react';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const AddressCard = ({ address, onEdit, onDelete, onSetPrimary, isDeleting }) => {
  const Icon = address.label?.toLowerCase() === 'home' ? Home : 
               address.label?.toLowerCase() === 'office' ? Building : Package;
  
  return (
    <div className={`relative p-4 rounded-xl border transition-all ${
      address.is_primary 
        ? 'bg-[#00BFFF]/10 border-[#00BFFF]/50' 
        : 'bg-zinc-900 border-zinc-800 hover:border-zinc-700'
    }`}>
      {/* Primary Badge */}
      {address.is_primary && (
        <div className="absolute -top-2 -right-2 px-2 py-1 bg-[#00BFFF] text-black text-xs font-bold rounded-full flex items-center gap-1">
          <Star className="w-3 h-3" />
          PRIMARY
        </div>
      )}
      
      {/* Locked Badge */}
      {address.locked && (
        <div className="absolute -top-2 left-4 px-2 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
          LOCKED
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          address.is_primary ? 'bg-[#00BFFF]/20' : 'bg-zinc-800'
        }`}>
          <Icon className={`w-5 h-5 ${address.is_primary ? 'text-[#00BFFF]' : 'text-zinc-400'}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-white">{address.label}</span>
            {address.is_validated ? (
              <span className="text-xs text-emerald-400 flex items-center gap-1">
                <Check className="w-3 h-3" /> Verified
              </span>
            ) : (
              <span className="text-xs text-amber-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Unverified
              </span>
            )}
          </div>
          
          <p className="text-zinc-300 text-sm">{address.name}</p>
          <p className="text-zinc-400 text-sm truncate">{address.street1}</p>
          {address.street2 && (
            <p className="text-zinc-400 text-sm truncate">{address.street2}</p>
          )}
          <p className="text-zinc-400 text-sm">
            {address.city}, {address.state} {address.zip}
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-800">
        {!address.is_primary && !address.locked && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => onSetPrimary(address.id)}
            className="text-zinc-400 hover:text-[#00BFFF] h-8"
            data-testid={`set-primary-btn-${address.id}`}
          >
            <Star className="w-4 h-4 mr-1" />
            Set Primary
          </Button>
        )}
        
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onEdit(address)}
          className="text-zinc-400 hover:text-white h-8"
          disabled={address.locked}
          data-testid={`edit-address-btn-${address.id}`}
        >
          <Edit2 className="w-4 h-4 mr-1" />
          Edit
        </Button>
        
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onDelete(address.id)}
          className="text-zinc-400 hover:text-red-400 h-8 ml-auto"
          disabled={address.locked || isDeleting}
          data-testid={`delete-address-btn-${address.id}`}
        >
          {isDeleting ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

const AddressForm = ({ address, onSave, onCancel, isLoading }) => {
  const [form, setForm] = useState({
    label: address?.label || 'Home',
    name: address?.name || '',
    street1: address?.street1 || '',
    street2: address?.street2 || '',
    city: address?.city || '',
    state: address?.state || '',
    zip: address?.zip || '',
    country: address?.country || 'US',
    phone: address?.phone || '',
    is_primary: address?.is_primary || false
  });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!form.name || !form.street1 || !form.city || !form.state || !form.zip) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!/^\d{5}(-\d{4})?$/.test(form.zip)) {
      toast.error('Please enter a valid ZIP code');
      return;
    }
    
    onSave(form);
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300">Label *</Label>
          <select
            value={form.label}
            onChange={(e) => setForm({...form, label: e.target.value})}
            className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
          >
            <option value="Home">Home</option>
            <option value="Office">Office</option>
            <option value="PO Box">PO Box</option>
            <option value="Other">Other</option>
          </select>
        </div>
        
        <div>
          <Label className="text-zinc-300">Recipient Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            placeholder="John Doe"
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
      </div>
      
      <div>
        <Label className="text-zinc-300">Street Address *</Label>
        <Input
          value={form.street1}
          onChange={(e) => setForm({...form, street1: e.target.value})}
          placeholder="123 Main St"
          className="mt-1 bg-zinc-800 border-zinc-700"
        />
      </div>
      
      <div>
        <Label className="text-zinc-300">Apt, Suite, Unit (Optional)</Label>
        <Input
          value={form.street2}
          onChange={(e) => setForm({...form, street2: e.target.value})}
          placeholder="Apt 4B"
          className="mt-1 bg-zinc-800 border-zinc-700"
        />
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-zinc-300">City *</Label>
          <Input
            value={form.city}
            onChange={(e) => setForm({...form, city: e.target.value})}
            placeholder="City"
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
        
        <div>
          <Label className="text-zinc-300">State *</Label>
          <select
            value={form.state}
            onChange={(e) => setForm({...form, state: e.target.value})}
            className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
          >
            <option value="">Select</option>
            {US_STATES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        
        <div>
          <Label className="text-zinc-300">ZIP *</Label>
          <Input
            value={form.zip}
            onChange={(e) => setForm({...form, zip: e.target.value})}
            placeholder="12345"
            className="mt-1 bg-zinc-800 border-zinc-700"
          />
        </div>
      </div>
      
      <div>
        <Label className="text-zinc-300">Phone (Optional)</Label>
        <Input
          value={form.phone}
          onChange={(e) => setForm({...form, phone: e.target.value})}
          placeholder="(555) 123-4567"
          className="mt-1 bg-zinc-800 border-zinc-700"
        />
      </div>
      
      {!address && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_primary"
            checked={form.is_primary}
            onChange={(e) => setForm({...form, is_primary: e.target.checked})}
            className="w-4 h-4 rounded border-zinc-700 bg-zinc-800"
          />
          <Label htmlFor="is_primary" className="text-zinc-300 cursor-pointer">
            Set as primary shipping address
          </Label>
        </div>
      )}
      
      <div className="flex gap-3 pt-4">
        <Button 
          type="submit" 
          className="flex-1 bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black"
          disabled={isLoading}
        >
          {isLoading ? <LoadingSpinner size="sm" /> : (address ? 'Update Address' : 'Add Address')}
        </Button>
        <Button 
          type="button"
          variant="outline"
          onClick={onCancel}
          className="border-zinc-700"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

const AddressManager = () => {
  const { isAuthenticated, api } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  const fetchAddresses = async () => {
    try {
      const res = await api().get('/addresses');
      setAddresses(res.data.addresses || []);
    } catch (e) {
      console.error('Failed to fetch addresses:', e);
    }
    setLoading(false);
  };
  
  useEffect(() => {
    if (isAuthenticated) {
      fetchAddresses();
    }
  }, [isAuthenticated]);
  
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingAddress) {
        await api().put(`/addresses/${editingAddress.id}`, formData);
        toast.success('Address updated successfully');
      } else {
        await api().post('/addresses', formData);
        toast.success('Address added successfully');
      }
      setShowForm(false);
      setEditingAddress(null);
      fetchAddresses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save address');
    }
    setSaving(false);
  };
  
  const handleDelete = async (addressId) => {
    setDeletingId(addressId);
    try {
      await api().delete(`/addresses/${addressId}`);
      toast.success('Address deleted');
      fetchAddresses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete address');
    }
    setDeletingId(null);
  };
  
  const handleSetPrimary = async (addressId) => {
    try {
      await api().post(`/addresses/${addressId}/set-primary`);
      toast.success('Primary address updated');
      fetchAddresses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set primary address');
    }
  };
  
  const handleEdit = (address) => {
    setEditingAddress(address);
    setShowForm(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[#00BFFF]" />
            Manage Addresses
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Save up to 5 shipping addresses. Your primary address will be used for auction purchases.
          </p>
        </div>
        
        {!showForm && addresses.length < 5 && (
          <Button 
            onClick={() => { setEditingAddress(null); setShowForm(true); }}
            className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black"
            data-testid="add-address-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Address
          </Button>
        )}
      </div>
      
      {/* Form */}
      {showForm && (
        <div className="p-6 bg-zinc-900 border border-zinc-800 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">
              {editingAddress ? 'Edit Address' : 'Add New Address'}
            </h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setShowForm(false); setEditingAddress(null); }}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <AddressForm
            address={editingAddress}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingAddress(null); }}
            isLoading={saving}
          />
        </div>
      )}
      
      {/* Address List */}
      {addresses.length === 0 && !showForm ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <MapPin className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Saved Addresses</h3>
          <p className="text-zinc-400 mb-4">
            Add a shipping address to participate in live auctions.
          </p>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Address
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {addresses.map(addr => (
            <AddressCard
              key={addr.id}
              address={addr}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onSetPrimary={handleSetPrimary}
              isDeleting={deletingId === addr.id}
            />
          ))}
        </div>
      )}
      
      {/* Info Box */}
      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <p className="text-amber-400 text-sm flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>
            <strong>Important:</strong> Addresses are locked after you win an auction to prevent fraud. 
            Make sure your primary address is correct before bidding.
          </span>
        </p>
      </div>
    </div>
  );
};

export default AddressManager;
